import {
  addToCart,
  CartError,
  clearGiftError,
  productForm,
  publishCartUpdate,
  selectedId,
  selectedIsAvailable,
  selectedInput,
  showGiftError,
} from '@theme/cart';
import { withPendingButton } from '@theme/utilities';

/**
 * The persistent add-to-bag bar: size picker, add button, and the desktop
 * slide-in once the inline buy button scrolls out of view.
 *
 * The cart half lives in `@theme/cart` now. This file used to carry its own
 * copies of the same seven functions `product-buy-buttons.js` carried —
 * addVariant, collectProperties, selectedInput, selectedId,
 * selectedIsAvailable, showGiftError, clearGiftError — copied almost line for
 * line. What is left is the bar itself.
 */
(function () {
  if (window.__stickyBuyInit) return;
  window.__stickyBuyInit = true;

  const bar = document.querySelector('[data-buy-sticky]');
  if (!bar) return;

  let variantMap = null;
  function getVariantMap() {
    if (variantMap) return variantMap;
    variantMap = {};
    const script = document.querySelector('[data-buy-variants]');
    if (script) {
      try { JSON.parse(script.textContent).forEach(function (v) { variantMap[v.id] = v; }); }
      catch (e) { /* noop */ }
    }
    return variantMap;
  }

  const stickyAdd = bar.querySelector('[data-buy-sticky-add]');
  const sizeToggle = bar.querySelector('[data-buy-size-toggle]');
  const sizeMenu = bar.querySelector('[data-buy-size-menu]');
  const priceEl = bar.querySelector('[data-buy-sticky-price]');
  const sizeLabel = bar.querySelector('[data-buy-size-label]');
  const addText = bar.getAttribute('data-add-text') || '';
  const soldOutText = bar.getAttribute('data-sold-out-text') || '';
  const notifyText = bar.getAttribute('data-notify-text') || '';

  // The back-in-stock drawer is rendered by the "Product options" block, and
  // only when it's switched on and Klaviyo is configured. On mobile the size
  // selector (and with it the stock row's "Notify me" link) is usually hidden,
  // so this bar is the only way to reach the drawer — when it exists.
  function notifyDrawer() { return document.querySelector('[data-notify-drawer]'); }

  // Reflect the chosen variant in the bar: price inside the add button, and
  // the selected size on the size toggle.
  function updateSticky(id) {
    if (!id) return;
    const v = getVariantMap()[id];
    if (!v) return;
    if (priceEl && v.price) { priceEl.textContent = v.price; priceEl.hidden = false; }
    if (sizeLabel && v.size) sizeLabel.textContent = v.size;
  }

  // Repurpose the add button for the selected size: "Add to bag" when it can
  // be bought, "Notify me" (opening the back-in-stock drawer) when it's sold
  // out and that drawer exists, and a disabled "Sold out" when it doesn't.
  // Runs on selection + at load.
  function updateState() {
    if (!stickyAdd) return;
    const input = selectedInput(productForm());
    const soldOut = !!input && input.getAttribute('data-variant-available') !== 'true';
    const drawer = soldOut ? notifyDrawer() : null;
    const label = stickyAdd.querySelector('.product-buy-sticky__add-label');
    if (label) label.textContent = soldOut ? (drawer ? notifyText : soldOutText) : addText;
    if (soldOut && priceEl) priceEl.hidden = true;

    // Carrying `data-drawer-open` lets assets/drawers.js own the open, exactly
    // as it does for the stock row's link — no second opening path to keep in
    // sync. Removed again as soon as the button goes back to adding to cart.
    if (drawer) stickyAdd.setAttribute('data-drawer-open', drawer.id);
    else stickyAdd.removeAttribute('data-drawer-open');

    stickyAdd.disabled = soldOut && !drawer;
    if (stickyAdd.disabled) stickyAdd.setAttribute('aria-disabled', 'true');
    else stickyAdd.removeAttribute('aria-disabled');
  }

  function openMenu() {
    if (!sizeMenu || !sizeToggle) return;
    sizeMenu.hidden = false;
    sizeToggle.setAttribute('aria-expanded', 'true');
  }
  function closeMenu() {
    if (!sizeMenu) return;
    sizeMenu.hidden = true;
    if (sizeToggle) sizeToggle.setAttribute('aria-expanded', 'false');
  }

  if (sizeToggle && sizeMenu) {
    sizeToggle.addEventListener('click', function (e) {
      e.stopPropagation();
      if (sizeMenu.hidden) openMenu();
      else closeMenu();
    });
  }

  async function doAdd(id, btn) {
    await withPendingButton(
      btn,
      async () => {
        try {
          // `sections` comes back with the add, so the drawer re-renders from it
          // instead of fetching the cart and then the section.
          const { sections } = await addToCart(id);
          clearGiftError();
          publishCartUpdate({ open: true, sections });
        } catch (e) {
          // The bar has no error area of its own, so a refusal goes to the
          // gift-card block's slot. Only a refusal: a network error's message is
          // "Failed to fetch", which is not shopper-facing copy.
          if (e instanceof CartError) showGiftError(e.message);
        }
      },
      { onSettled: updateState }
    );
  }

  if (stickyAdd) {
    stickyAdd.addEventListener('click', function (e) {
      const f = productForm();
      const id = selectedId(f);
      if (id) {
        // Sold-out size selected: never add to cart. If the back-in-stock
        // drawer is available the button carries `data-drawer-open` and
        // drawers.js opens it off this same click.
        if (!selectedIsAvailable(f)) return;
        doAdd(id, stickyAdd);
        return;
      }
      // No size selected yet: open the size dropdown to choose one.
      e.stopPropagation();
      openMenu();
    });
  }

  // Dropdown size selection: select the size into the shared form (so the
  // stock row, the size button and the bar's price all update), then close.
  document.addEventListener('click', function (e) {
    const opt = e.target.closest('[data-buy-size-option]');
    if (!opt) return;
    e.preventDefault();
    // Sold-out sizes are selectable too (like the desktop grid) — selecting
    // one surfaces the sold-out state; purchase stays blocked by updateState.
    const id = opt.getAttribute('data-variant-id');
    if (!id) return;
    const radio = document.querySelector('input[name="id"][value="' + id + '"]');
    if (radio) {
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
    closeMenu();
  });

  // Close the dropdown on outside click / Escape.
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-buy-size-wrap]')) return;
    closeMenu();
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeMenu();
  });

  // Keep the bar's price + size in sync with the chosen variant.
  document.addEventListener('variant:change', function (e) {
    const id = e.detail && e.detail.id;
    if (id) updateSticky(id);
    updateState();
  });

  // Desktop: reveal the bar once the inline add button has scrolled PAST the
  // top of the viewport. On mobile the bar is always visible via CSS, so the
  // class is a no-op.
  //
  // Direction matters. The first version showed the bar whenever the button
  // was not on screen, with the root trimmed 20% at the bottom. On a short
  // laptop viewport the button sits below the fold at load, so the bar slid in
  // immediately, hid again once the button scrolled into the top 80%, and
  // reappeared once it left through the top — three states for one scroll.
  // "Not visible AND above the viewport" is the only case that means "the
  // shopper has passed the inline button"; below the fold it is simply not
  // reached yet, and the bar stays put.
  //
  // The root is extended one viewport downwards. The observer only calls back
  // when the intersection state CHANGES, and the button is 33px tall: a fast
  // wheel scroll can carry it from below the viewport to above it between two
  // frames without ever intersecting, and the bar would never learn it had
  // been passed. With the extended root the button is "intersecting" from one
  // viewport below the fold up to the top edge, so leaving through the top is
  // always a state change.
  const inlineSubmit = document.querySelector('[data-buy-submit]');
  if (inlineSubmit && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        const passed = !en.isIntersecting && en.boundingClientRect.bottom <= 0;
        bar.classList.toggle('is-visible', passed);
        // When the bar slides out of view (desktop), close the size dropdown
        // so it doesn't stay floating over the page.
        if (!passed) closeMenu();
      });
    }, { rootMargin: '0px 0px 100% 0px' });
    io.observe(inlineSubmit);
  } else {
    // No inline button to track (e.g. block removed): keep the bar visible.
    bar.classList.add('is-visible');
  }

  // Reflect the current selection at load (single-variant, or a checked size).
  updateSticky(selectedId());
  updateState();
})();
